import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import {
  INVOICE_DELETABLE_STATUSES,
  type Invoice,
  type LedgerSnapshot,
  type RawOcr,
  type TdsProfile,
  type ValidationFinding,
} from '@fpc/shared';
import { api, apiClient } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useMoney } from '@/hooks/useMoney';
import { PurchaseOrderMatch } from '@/components/PurchaseOrderMatch';
import { ExpenseLedgerOptions, useExpenseLedgers } from '@/components/ExpenseLedgerOptions';
import {
  TdsLinesEditor,
  draftsOf,
  parseLines,
  withVendorProfiles,
  type LineDraft,
} from '@/components/TdsLinesEditor';
import { useTdsSections } from '@/hooks/useTdsSections';
import { RcmCheckbox, RcmSummary } from '@/components/RcmFields';
import { describeError } from '@/lib/errors';
import { EXTRACTION_POLL_MS, isExtracting, useExtractionStall } from '@/lib/extraction';
import { formatDate, formatDateTime, humanize, rupeesToMinor } from '@/lib/format';
import {
  comparableFieldValue,
  computeTdsLines,
  differsFromExtracted,
  EDITABLE_FIELDS,
  formatBasisPoints,
  fromMinor,
  invoiceNetPayableFor,
  normalizeName,
  parseAmountToMinor,
  tdsBaseFor,
  totalTds,
  toIsoDate,
  type EditableField,
  type TdsLineInput,
} from '@fpc/shared';
import {
  Card,
  ConfidenceBadge,
  ConfirmWithReason,
  EditedBadge,
  ErrorState,
  Modal,
  Money,
  PageHeader,
  Spinner,
  StatusBadge,
  Tabs,
} from '@/components/ui';

/**
 * Invoice review and detail — PRD §12, §13.
 *
 * The document sits beside the extracted fields so a reviewer can check a
 * value against the page without switching context, and every field shows the
 * confidence it was extracted with, so attention goes where the machine was
 * unsure rather than being spread evenly.
 */
export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, user } = useAuth();
  const [resolving, setResolving] = useState<ValidationFinding | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const wasExtracting = useRef(false);
  const stalledRef = useRef(false);

  // An invoice opened straight after an upload or an email is still being read:
  // the server answers the upload before extraction starts. Ask again until the
  // reading is done, then stop -- and stop at the ceiling rather than forever.
  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.invoices.get(id),
    refetchInterval: (query) =>
      isExtracting(query.state.data?.status) && !stalledRef.current ? EXTRACTION_POLL_MS : false,
  });
  const extracting = isExtracting(invoice?.status);
  const stall = useExtractionStall(extracting);
  useEffect(() => {
    stalledRef.current = stall.stalled;
  }, [stall.stalled]);

  // The audit trail gains "extracted" the moment the reading finishes, and it is
  // a separate query, so it has to be asked again then.
  useEffect(() => {
    if (wasExtracting.current && !extracting) {
      void queryClient.invalidateQueries({ queryKey: ['audit', 'INVOICE', id] });
    }
    wasExtracting.current = extracting;
  }, [extracting, id, queryClient]);

  const { data: audit } = useQuery({
    queryKey: ['audit', 'INVOICE', id],
    queryFn: () => api.audit.forEntity('INVOICE', id),
    enabled: can('audit:read'),
  });

  const { data: vendors } = useQuery({
    queryKey: ['vendors', invoice?.companyId],
    queryFn: () => api.settings.vendors({ companyId: invoice!.companyId, pageSize: 200 }),
    enabled: !!invoice?.companyId && can('vendor:read'),
  });

  const submit = useMutation({
    mutationFn: () => api.invoices.submit(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => api.invoices.cancel(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setCancelling(false);
    },
  });

  // Gone from every list once deleted, so there is nothing left to show here.
  const remove = useMutation({
    mutationFn: (reason: string) => api.invoices.delete(id, reason),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/invoices', { replace: true });
    },
  });

  // Re-running extraction discards reviewer edits, so it is offered only
  // where those edits do not exist yet or have already failed.
  const reextract = useMutation({
    mutationFn: () => api.invoices.reextract(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });

  if (isLoading) return <Spinner />;
  // A failed background re-check must not replace a page that is already showing
  // the invoice; only a failure to load it in the first place is an error page.
  if (error && !invoice) return <ErrorState error={error} />;
  if (!invoice) return null;

  const blocking = (invoice.findings ?? []).filter(
    (finding) => !finding.resolved && finding.severity !== 'INFO',
  );
  const editable = ['REVIEW_REQUIRED', 'VALIDATED', 'FAILED', 'REJECTED'].includes(invoice.status);
  // Before submission, its uploader may delete it; so may whoever holds invoice:delete.
  const canDelete =
    INVOICE_DELETABLE_STATUSES.includes(invoice.status) &&
    ((!!user && invoice.uploadedBy === user.userId && can('invoice:create')) ||
      can('invoice:delete'));

  return (
    <>
      <PageHeader
        title={invoice.invoiceNumber ?? 'Invoice'}
        subtitle={
          <span className="flex flex-wrap items-center gap-3">
            <StatusBadge status={invoice.status} />
            {/* The identifier a person quotes on the phone or in an email. */}
            <span className="font-mono text-xs text-slate-500">{invoice.trackingId}</span>
            <span>{invoice.vendorName ?? 'Vendor not set'}</span>
            <span>·</span>
            <span>Received {formatDateTime(invoice.receivedAt)}</span>
          </span>
        }
        actions={
          <>
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
            {editable && can('invoice:update') ? (
              <button
                className="btn-secondary"
                disabled={reextract.isPending}
                title="Read the document again, discarding any manual corrections"
                onClick={() => reextract.mutate()}
              >
                {reextract.isPending ? 'Queued…' : 'Re-run extraction'}
              </button>
            ) : null}
            {canDelete ? (
              <button className="btn-secondary" onClick={() => setDeleting(true)}>
                Delete
              </button>
            ) : null}
            {can('invoice:cancel') &&
            !['PAID', 'RECONCILED', 'CANCELLED'].includes(invoice.status) ? (
              <button className="btn-secondary" onClick={() => setCancelling(true)}>
                Cancel invoice
              </button>
            ) : null}
            {editable && can('invoice:submit') ? (
              <button
                className="btn-primary"
                disabled={blocking.length > 0 || submit.isPending}
                title={blocking.length ? 'Resolve the findings below first' : undefined}
                onClick={() => submit.mutate()}
              >
                {submit.isPending ? 'Submitting…' : 'Submit for approval'}
              </button>
            ) : null}
          </>
        }
      />

      {/* Mounted once extraction is done so its search starts from the extracted fields. */}
      {extracting ? null : (
        <PurchaseOrderMatch invoice={invoice} editable={editable && can('invoice:update')} />
      )}

      {invoice.rawOcr ? <RawExtractedForm rawOcr={invoice.rawOcr} /> : null}

      {submit.error ? (
        <div className="mb-4">
          <ErrorState error={submit.error} />
        </div>
      ) : null}

      {submit.data?.autoApprovedReason ? (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Approved automatically: {submit.data.autoApprovedReason}. Configure an approval rule in
          Settings if this should have required sign-off.
        </div>
      ) : null}

      {invoice.findings?.length ? (
        <div className="mb-6 space-y-2">
          {invoice.findings.map((finding) => (
            <FindingBanner
              key={`${finding.code}-${finding.field ?? ''}`}
              finding={finding}
              canResolve={can('invoice:resolve_duplicate') && editable}
              onResolve={() => setResolving(finding)}
            />
          ))}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {extracting ? (
          <ExtractionProgress
            stalled={stall.stalled}
            onCheckAgain={() => {
              stall.retry();
              void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
            }}
          />
        ) : (
          // Mounted only once the reading is done: the form takes its starting
          // values from the invoice it is first given, so mounting it earlier
          // would leave it blank after the fields arrive.
          <InvoiceFields
            invoice={invoice}
            editable={editable && can('invoice:update')}
            vendors={vendors?.items ?? []}
          />
        )}
        <DocumentViewer invoiceId={invoice.id} fileName={invoice.documentFileName} />
      </div>

      <InvoiceCoding
        invoice={invoice}
        editable={editable && can('invoice:update')}
        frozen={!editable}
      />

      {invoice.lines?.length ? (
        <Card className="mt-6">
          <div className="border-b border-slate-200 px-5 py-3">
            <h2 className="font-semibold">Line items</h2>
          </div>
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="thead">
              <tr>
                <th className="th">Description</th>
                <th className="th text-right">Qty</th>
                <th className="th text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoice.lines.map((line, index) => (
                <tr key={index}>
                  <td className="td whitespace-normal">{line.description}</td>
                  <td className="td text-right">{line.quantity ?? '—'}</td>
                  <td className="td text-right">
                    <Money minor={line.amount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      {audit?.items.length ? (
        <Card className="mt-6">
          <div className="border-b border-slate-200 px-5 py-3">
            <h2 className="font-semibold">Audit trail</h2>
          </div>
          <ol className="divide-y divide-slate-100">
            {audit.items.map((event) => (
              <li key={event.id} className="flex gap-4 px-5 py-3 text-sm">
                <span className="w-40 shrink-0 text-slate-500">
                  {formatDateTime(event.timestamp)}
                </span>
                <span className="flex-1">
                  <span className="font-medium">{humanize(event.event.split('.').pop())}</span>
                  {event.userName ? (
                    <span className="text-slate-500"> · {event.userName}</span>
                  ) : null}
                  {event.metadata?.comment ? (
                    <p className="mt-1 text-slate-600">“{String(event.metadata.comment)}”</p>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {deleting ? (
        <ConfirmWithReason
          title="Delete this invoice"
          actionLabel="Delete invoice"
          description={
            <>
              <p>
                {invoice.vendorName ?? 'This invoice'} {invoice.invoiceNumber ?? ''} will be removed
                from every list, so it can be uploaded again. Its history stays in the audit trail.
              </p>
              <p className="mt-2">Only possible until it is submitted for approval.</p>
            </>
          }
          pending={remove.isPending}
          error={remove.error}
          onClose={() => setDeleting(false)}
          onConfirm={(reason) => remove.mutate(reason)}
        />
      ) : null}

      {cancelling ? (
        <ConfirmWithReason
          title="Cancel this invoice"
          actionLabel="Cancel invoice"
          description={
            <>
              <p>
                {invoice.vendorName ?? 'This invoice'} {invoice.invoiceNumber ?? ''} will be
                cancelled and can no longer be paid. Any approval still in progress is withdrawn.
              </p>
              <p className="mt-2">This cannot be undone.</p>
            </>
          }
          pending={cancel.isPending}
          error={cancel.error}
          onClose={() => setCancelling(false)}
          onConfirm={(reason) => cancel.mutate(reason)}
        />
      ) : null}

      {resolving ? (
        <ResolveFindingModal
          invoiceId={invoice.id}
          finding={resolving}
          onClose={() => setResolving(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Stands in for the fields while the document is being read.
 *
 * Wording is deliberately about the outcome, not the engine: the same card
 * covers the credential-free stub, Claude and Azure Document Intelligence.
 */
/**
 * The text the uploader's browser read, exactly as it came, so a reviewer can
 * check a matched field against what OCR actually saw. Collapsed by default:
 * it is reference material, not the thing being reviewed.
 */
function RawExtractedForm({ rawOcr }: { rawOcr: RawOcr }) {
  const [open, setOpen] = useState(false);
  const confidence = weightedConfidence(rawOcr);
  // Uploads from before layouts were kept have only the plain text.
  const formatted = rawOcr.pages.every((page) => page.layout?.trim());
  const [view, setView] = useState<'FORMATTED' | 'RAW'>(formatted ? 'FORMATTED' : 'RAW');

  return (
    <div className="card mb-6">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
        aria-expanded={open}
        aria-controls="raw-extracted-form"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="font-semibold text-ink-900">Raw extracted form</span>
        <ChevronRight
          className={`h-5 w-5 text-ink-500 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div id="raw-extracted-form" className="space-y-4 border-t border-ink-100 px-5 py-4">
          <p className="flex flex-wrap items-center gap-2 text-sm text-ink-500">
            <span>
              {readBy(rawOcr)} ·{' '}
              {rawOcr.pages.length === 1 ? '1 page' : `${rawOcr.pages.length} pages`} ·{' '}
              {formatDateTime(rawOcr.extractedAt)}
            </span>
            <ConfidenceBadge value={confidence} />
          </p>

          {formatted ? (
            <Tabs<'FORMATTED' | 'RAW'>
              tabs={[
                { key: 'FORMATTED', label: 'Formatted' },
                { key: 'RAW', label: 'Raw text' },
              ]}
              active={view}
              onChange={setView}
            />
          ) : null}

          {view === 'FORMATTED' && formatted ? (
            // The fields are matched on this view: each page laid out as printed,
            // so a value sits beside or under its label as on paper.
            rawOcr.pages.map((page) => (
              <section key={page.page}>
                {rawOcr.pages.length > 1 ? (
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-700">
                    Page {page.page} <ConfidenceBadge value={page.confidence} />
                  </h3>
                ) : null}
                <pre
                  data-testid="formatted-page"
                  className="max-h-[36rem] overflow-auto whitespace-pre rounded-xl border border-ink-200 bg-ink-50 p-4 font-mono text-xs leading-5 text-ink-800"
                >
                  {page.layout}
                </pre>
              </section>
            ))
          ) : (
            <>
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink-700">Extracted text</h3>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-xl border border-ink-200 bg-ink-50 p-4 text-sm text-ink-800">
                  {rawOcr.text}
                </pre>
              </section>

              {rawOcr.pages.length > 1
                ? rawOcr.pages.map((page) => (
                    <section key={page.page}>
                      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-700">
                        Page {page.page} <ConfidenceBadge value={page.confidence} />
                      </h3>
                      <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-xl border border-ink-200 bg-ink-50 p-4 text-sm text-ink-800">
                        {page.text}
                      </pre>
                    </section>
                  ))
                : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function readBy(rawOcr: RawOcr): string {
  if (rawOcr.engine === 'pdf.js') return "Read from the PDF's own text in the uploader's browser";
  return `Read in the uploader's browser by ${rawOcr.engine}`;
}

/** 0-100, weighted by how much text each page carried. Mirrors the server's scaling. */
function weightedConfidence(rawOcr: RawOcr): number {
  const weights = rawOcr.pages.map((page) => page.text.trim().length);
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (!total) return 0;
  return (
    rawOcr.pages.reduce((sum, page, index) => sum + page.confidence * weights[index]!, 0) / total
  );
}

function ExtractionProgress({ stalled, onCheckAgain }: { stalled: boolean; onCheckAgain(): void }) {
  return (
    <Card className="p-5">
      <h2 className="font-semibold">Invoice details</h2>
      {stalled ? (
        <div role="status" className="py-10 text-center text-sm text-slate-600">
          <p className="font-medium text-slate-800">This is taking longer than usual.</p>
          <p className="mt-1">
            The document is still queued for reading and will be retried automatically.
          </p>
          <button className="btn-secondary mt-4" onClick={onCheckAgain}>
            Check again
          </button>
        </div>
      ) : (
        <>
          <Spinner label="Reading your invoice…" />
          <p className="pb-6 text-center text-sm text-slate-500">
            Extracting the vendor, invoice number, dates and amounts. This page updates by itself
            when they are ready.
          </p>
        </>
      )}
    </Card>
  );
}

function FindingBanner({
  finding,
  canResolve,
  onResolve,
}: {
  finding: ValidationFinding;
  canResolve: boolean;
  onResolve(): void;
}) {
  if (finding.resolved) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-600">
        <span className="font-medium">Resolved:</span> {finding.message}
        {finding.resolutionNote ? (
          <span className="text-slate-500"> — “{finding.resolutionNote}”</span>
        ) : null}
      </div>
    );
  }

  const tone =
    finding.severity === 'ERROR'
      ? 'border-red-200 bg-red-50 text-red-800'
      : finding.severity === 'WARNING'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-blue-200 bg-blue-50 text-blue-900';

  return (
    <div
      className={`flex items-start justify-between gap-4 rounded-md border px-4 py-3 text-sm ${tone}`}
    >
      <span>
        <span className="font-medium">{humanize(finding.code)}</span> — {finding.message}
      </span>
      {canResolve && finding.severity !== 'INFO' ? (
        <button className="btn-secondary shrink-0" onClick={onResolve}>
          Resolve
        </button>
      ) : null}
    </div>
  );
}

function InvoiceFields({
  invoice,
  editable,
  vendors,
}: {
  invoice: Invoice;
  editable: boolean;
  vendors: Array<{ id: string; name: string; tdsProfiles?: TdsProfile[] }>;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    vendorId: invoice.vendorId ?? '',
    invoiceNumber: invoice.invoiceNumber ?? '',
    invoiceDate: invoice.invoiceDate?.slice(0, 10) ?? '',
    dueDate: invoice.dueDate?.slice(0, 10) ?? '',
    gstin: invoice.gstin ?? '',
    pan: invoice.pan ?? '',
    subtotal: invoice.subtotal !== undefined ? String(fromMinor(invoice.subtotal)) : '',
    taxAmount: invoice.taxAmount !== undefined ? String(fromMinor(invoice.taxAmount)) : '',
    totalAmount: invoice.totalAmount !== undefined ? String(fromMinor(invoice.totalAmount)) : '',
  });
  // Through useMoney: it falls back to rupees wherever the company is not loaded yet.
  const { company, format } = useMoney();

  // TDS as keyed at review: each line's section and rate from Settings → TDS
  // Sections. Accounting confirms or changes it at verification.
  const [tdsOn, setTdsOn] = useState(invoice.tdsApplicable);
  const [tdsDrafts, setTdsDrafts] = useState<LineDraft[]>(() => draftsOf(invoice));
  const { sections: tdsSections, ratesFor, successorOf } = useTdsSections();
  // RCM ticked at review; accounting confirms it.
  const [rcm, setRcm] = useState(!!invoice.rcmApplicable);
  // A vendor's RCM default arrives with the saved invoice: show what it now carries.
  useEffect(() => {
    setRcm(!!invoice.rcmApplicable);
  }, [invoice.rcmApplicable]);
  const rcmDirty = rcm !== !!invoice.rcmApplicable;

  // The expense ledger suggested for accounting, beside the due date: the
  // vendor's default, or one chosen here. Accounting decides at verification.
  const { can } = useAuth();
  const canTag = can('ledger:read');
  const { ledgers: expenseLedgers } = useExpenseLedgers(invoice.companyId, canTag);
  const ledgerVendor = useQuery({
    queryKey: ['vendor', invoice.vendorId],
    queryFn: () => api.settings.vendor(invoice.vendorId!),
    enabled: !!invoice.vendorId && canTag && can('vendor:read'),
  }).data;
  const vendorDefault = expenseLedgers.find(
    (ledger) => ledger.id === ledgerVendor?.defaultExpenseLedgerId,
  );
  const [suggestedLedgerId, setSuggestedLedgerId] = useState(
    invoice.suggestedExpenseLedger?.id ?? '',
  );
  // Re-extraction or a vendor change can propose a new ledger while the page is open.
  useEffect(() => {
    setSuggestedLedgerId(invoice.suggestedExpenseLedger?.id ?? '');
  }, [invoice.suggestedExpenseLedger?.id]);
  const ledgerDirty = canTag && suggestedLedgerId !== (invoice.suggestedExpenseLedger?.id ?? '');
  // Where the saved suggestion came from, while it is what the form shows.
  const savedSuggestion = invoice.suggestedExpenseLedger;
  const suggestionSource =
    savedSuggestion && suggestedLedgerId === savedSuggestion.id
      ? savedSuggestion.source === 'VENDOR_DEFAULT'
        ? 'Filled from the vendor’s default'
        : 'Chosen at review'
      : !savedSuggestion && !suggestedLedgerId && vendorDefault
        ? 'The vendor’s default'
        : undefined;
  const ledgerLabel = (ledger: { code: string; name: string }) => `${ledger.code} · ${ledger.name}`;
  const tdsProfiles =
    vendors.find((vendor) => vendor.id === (draft.vendorId || invoice.vendorId))?.tdsProfiles ?? [];
  const taxable = tdsBaseFor({
    subtotal: rupeesToMinor(draft.subtotal) ?? invoice.subtotal,
    taxAmount: rupeesToMinor(draft.taxAmount) ?? invoice.taxAmount,
    totalAmount: rupeesToMinor(draft.totalAmount) ?? invoice.totalAmount,
  });
  const tdsKeyed = parseLines(tdsDrafts).filter(
    (line) => line.section && line.rateBasisPoints > 0 && line.baseAmount > 0,
  );
  const lineKey = (
    line: Pick<TdsLineInput, 'section' | 'rateBasisPoints' | 'baseAmount' | 'label'>,
  ) => [line.section, line.rateBasisPoints, line.baseAmount, line.label ?? ''].join('|');
  const tdsDirty =
    tdsOn !== invoice.tdsApplicable ||
    (tdsOn && tdsKeyed.map(lineKey).join() !== (invoice.tdsLines ?? []).map(lineKey).join());
  const mismatch = invoice.findings?.find(
    (finding) => finding.code === 'CURRENCY_MISMATCH' && !finding.resolved,
  );
  const documentCurrency = invoice.extraction?.fields?.currency?.value;

  // Confirms the document really is billed in the company's currency — the
  // way to clear a mismatch raised on a misread one.
  const confirmCurrency = useMutation({
    mutationFn: () => api.invoices.update(invoice.id, { currency: invoice.currency }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] }),
  });

  // The draft in the invoice's own form (minor units, yyyy-mm-dd, the vendor's
  // name), which is what the extracted reading is compared with.
  const vendorNameOf = (id: string) =>
    vendors.find((vendor) => vendor.id === id)?.name ??
    (id === invoice.vendorId ? invoice.vendorName : undefined);
  const drafted: Record<EditableField, unknown> = {
    vendorName: draft.vendorId ? vendorNameOf(draft.vendorId) : undefined,
    invoiceNumber: draft.invoiceNumber,
    invoiceDate: draft.invoiceDate,
    dueDate: draft.dueDate,
    subtotal: rupeesToMinor(draft.subtotal),
    taxAmount: rupeesToMinor(draft.taxAmount),
    totalAmount: rupeesToMinor(draft.totalAmount),
    gstin: draft.gstin.trim().toUpperCase(),
    pan: draft.pan.trim().toUpperCase(),
  };

  /** Changed in this form since the invoice was last saved. */
  const dirty = (field: EditableField): boolean => {
    switch (field) {
      case 'vendorName':
        return draft.vendorId !== (invoice.vendorId ?? '');
      case 'subtotal':
      case 'taxAmount':
      case 'totalAmount':
        return drafted[field] !== null && drafted[field] !== invoice[field];
      case 'invoiceDate':
      case 'dueDate':
        return draft[field] !== (invoice[field]?.slice(0, 10) ?? '');
      case 'gstin':
        return drafted.gstin !== (invoice.gstin ?? '');
      case 'pan':
        return drafted.pan !== (invoice.pan ?? '');
      default:
        return draft[field] !== (invoice[field] ?? '');
    }
  };

  const save = useMutation({
    mutationFn: () => {
      // Only what this form changed: the server marks a field edited by
      // comparing it with the reading, and a save speaks only for what it sent.
      const body: Record<string, unknown> = {};
      if (dirty('vendorName') && draft.vendorId) body.vendorId = draft.vendorId;
      if (dirty('invoiceNumber') && draft.invoiceNumber) body.invoiceNumber = draft.invoiceNumber;
      if (dirty('invoiceDate') && draft.invoiceDate) body.invoiceDate = draft.invoiceDate;
      if (dirty('dueDate') && draft.dueDate) body.dueDate = draft.dueDate;
      if (dirty('gstin')) body.gstin = drafted.gstin;
      if (dirty('pan')) body.pan = drafted.pan;
      if (tdsDirty) {
        body.tdsApplicable = tdsOn && tdsKeyed.length > 0;
        body.tdsLines = tdsOn ? tdsKeyed : [];
      }
      if (rcmDirty) body.rcmApplicable = rcm;
      if (ledgerDirty) body.suggestedExpenseLedgerId = suggestedLedgerId;
      for (const field of ['subtotal', 'taxAmount', 'totalAmount'] as const) {
        if (dirty(field)) body[field] = drafted[field];
      }
      return api.invoices.update(invoice.id, body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] }),
  });

  const reading = (field: string) => invoice.extraction?.fields?.[field];
  const confidence = (field: string): number | undefined => reading(field)?.confidence;
  const learned = (field: string): boolean => {
    const extracted = reading(field);
    return extracted?.source === 'LEARNED' && !extracted.edited;
  };

  /** What extraction read for the field, in the form this page holds it — for "Revert". */
  const readingAsDraft = (field: EditableField): string | undefined => {
    const value = reading(field)?.value;
    if (value === null || value === undefined || value === '') return undefined;
    switch (field) {
      case 'vendorName':
        return vendors.find((vendor) => normalizeName(vendor.name) === normalizeName(String(value)))
          ?.id;
      case 'subtotal':
      case 'taxAmount':
      case 'totalAmount': {
        const minor = parseAmountToMinor(value);
        return minor === null ? undefined : String(fromMinor(minor));
      }
      case 'invoiceDate':
      case 'dueDate':
        return toIsoDate(String(value));
      case 'gstin':
      case 'pan':
        return String(value).trim().toUpperCase();
      default:
        return String(value).trim();
    }
  };

  /**
   * Whether a field holds what extraction read, or a person's value. While
   * it is being edited here the draft decides, so the mark appears as the
   * reviewer types; otherwise the server's mark does, which is what an
   * approver sees.
   */
  const provenance = (field: EditableField): Provenance | undefined => {
    if (!invoice.extraction) return undefined;
    const extracted = reading(field);
    const changed =
      editable && dirty(field)
        ? differsFromExtracted(field, extracted?.value, drafted[field])
        : Boolean(extracted?.edited);
    if (!changed) return undefined;

    const read = comparableFieldValue(field, extracted?.value, 'extracted');
    if (read === undefined) return { kind: 'manual', unsaved: dirty(field) };
    const revertTo = editable ? readingAsDraft(field) : undefined;
    const key = field === 'vendorName' ? 'vendorId' : field;
    return {
      kind: 'edited',
      unsaved: editable && dirty(field),
      original:
        typeof read === 'number' ? (
          <Money currency={invoice.currency} minor={read} />
        ) : field === 'invoiceDate' || field === 'dueDate' ? (
          formatDate(read)
        ) : (
          String(extracted?.value)
        ),
      onRevert:
        revertTo === undefined
          ? undefined
          : () => setDraft((current) => ({ ...current, [key]: revertTo })),
    };
  };
  const marks = Object.fromEntries(
    EDITABLE_FIELDS.map((field) => [field, provenance(field)]),
  ) as Record<EditableField, Provenance | undefined>;
  const tint = (field: EditableField) => (marks[field] ? ' input-edited' : '');
  const showTds = company.tds || invoice.tdsApplicable || invoice.tdsAmount > 0;

  // What the bank pays with the form as it stands — gross less TDS, RCM and
  // advance — so the preview tracks the values being edited.
  const editingTds = editable;
  const formTotal = (drafted.totalAmount as number | null) ?? invoice.totalAmount ?? 0;
  const formTds = editingTds
    ? tdsOn
      ? Math.min(totalTds(computeTdsLines(tdsKeyed)), formTotal)
      : 0
    : (invoice.tdsAmount ?? 0);
  const formTax = editable ? ((drafted.taxAmount as number | null) ?? 0) : (invoice.taxAmount ?? 0);
  const formRcm = rcm ? Math.max(0, formTax) : 0;
  const netPayable =
    editingTds || dirty('totalAmount') || dirty('taxAmount') || rcm !== invoice.rcmApplicable
      ? invoiceNetPayableFor(formTotal, formTds, formRcm, invoice.advanceAdjusted ?? 0)
      : invoice.netPayable;
  const netUnsaved = netPayable !== invoice.netPayable;

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold">Invoice details</h2>
        {invoice.extraction ? (
          <span className="text-xs text-slate-500">
            Extracted by {invoice.extraction.provider}
            {invoice.extraction.model ? ` (${invoice.extraction.model})` : ''}
          </span>
        ) : null}
      </div>
      {invoice.extraction ? (
        <p className="-mt-2 mb-4 text-xs text-slate-500">
          <span className="badge badge-positive rounded-md px-1.5">Auto · 95%</span> filled in by
          extraction, with how sure it was. <EditedBadge kind="edited" /> changed by a reviewer,
          with what was read beneath it. <EditedBadge kind="manual" /> typed in where nothing was
          read.
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Vendor" confidence={confidence('vendorName')} edit={marks.vendorName}>
          {editable ? (
            <select
              className={`input${tint('vendorName')}`}
              value={draft.vendorId}
              onChange={(event) => setDraft({ ...draft, vendorId: event.target.value })}
            >
              <option value="">Select a vendor…</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm">{invoice.vendorName ?? '—'}</p>
          )}
        </Field>

        <Field
          label="Invoice number"
          confidence={confidence('invoiceNumber')}
          edit={marks.invoiceNumber}
          learned={learned('invoiceNumber')}
        >
          {editable ? (
            <input
              className={`input${tint('invoiceNumber')}`}
              value={draft.invoiceNumber}
              onChange={(event) => setDraft({ ...draft, invoiceNumber: event.target.value })}
            />
          ) : (
            <p className="text-sm">{invoice.invoiceNumber ?? '—'}</p>
          )}
        </Field>

        <Field
          label="Invoice date"
          confidence={confidence('invoiceDate')}
          edit={marks.invoiceDate}
          learned={learned('invoiceDate')}
        >
          {editable ? (
            <input
              type="date"
              className={`input${tint('invoiceDate')}`}
              value={draft.invoiceDate}
              onChange={(event) => setDraft({ ...draft, invoiceDate: event.target.value })}
            />
          ) : (
            <p className="text-sm">{formatDate(invoice.invoiceDate)}</p>
          )}
        </Field>

        <Field
          label="Due date"
          confidence={confidence('dueDate')}
          edit={marks.dueDate}
          learned={learned('dueDate')}
        >
          {editable ? (
            <input
              type="date"
              className={`input${tint('dueDate')}`}
              value={draft.dueDate}
              onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
            />
          ) : (
            <p className="text-sm">{formatDate(invoice.dueDate)}</p>
          )}
        </Field>

        {canTag ? (
          <Field label="Expense ledger (suggested)" confidence={undefined}>
            {editable ? (
              <select
                className="input"
                aria-label="Expense ledger (suggested)"
                value={suggestedLedgerId}
                onChange={(event) => setSuggestedLedgerId(event.target.value)}
              >
                <option value="">
                  {vendorDefault
                    ? `Vendor default — ${ledgerLabel(vendorDefault)}`
                    : 'No suggestion'}
                </option>
                <ExpenseLedgerOptions ledgers={expenseLedgers} />
              </select>
            ) : (
              <p className="text-sm">
                {invoice.suggestedExpenseLedger
                  ? ledgerLabel(invoice.suggestedExpenseLedger)
                  : vendorDefault
                    ? ledgerLabel(vendorDefault)
                    : '—'}
              </p>
            )}
            {suggestionSource ? (
              <p className="mt-1 text-xs text-slate-500">{suggestionSource}</p>
            ) : null}
            {editable ? (
              <p className="mt-1 text-xs text-slate-500">
                Accounting confirms or changes it at verification.
              </p>
            ) : null}
          </Field>
        ) : null}

        <Field
          label="Taxable Value"
          confidence={confidence('subtotal')}
          edit={marks.subtotal}
          learned={learned('subtotal')}
        >
          {editable ? (
            <input
              className={`input tabular${tint('subtotal')}`}
              inputMode="decimal"
              value={draft.subtotal}
              onChange={(event) => setDraft({ ...draft, subtotal: event.target.value })}
            />
          ) : (
            <Money currency={invoice.currency} minor={invoice.subtotal} />
          )}
        </Field>

        <Field
          label="Tax"
          confidence={confidence('taxAmount')}
          edit={marks.taxAmount}
          learned={learned('taxAmount')}
        >
          {editable ? (
            <>
              <input
                className={`input tabular${tint('taxAmount')}`}
                inputMode="decimal"
                value={draft.taxAmount}
                onChange={(event) => setDraft({ ...draft, taxAmount: event.target.value })}
              />
              {company.india ? (
                <RcmCheckbox
                  checked={rcm}
                  onChange={setRcm}
                  gst={Math.max(0, (drafted.taxAmount as number | null) ?? 0)}
                  currency={invoice.currency}
                  compact
                />
              ) : null}
            </>
          ) : (
            <>
              <Money currency={invoice.currency} minor={invoice.taxAmount} />
              {company.india && invoice.rcmApplicable ? <RcmSummary invoice={invoice} /> : null}
            </>
          )}
        </Field>

        <Field
          label="Total"
          confidence={confidence('totalAmount')}
          edit={marks.totalAmount}
          learned={learned('totalAmount')}
        >
          {editable ? (
            <input
              className={`input tabular font-semibold${tint('totalAmount')}`}
              inputMode="decimal"
              value={draft.totalAmount}
              onChange={(event) => setDraft({ ...draft, totalAmount: event.target.value })}
            />
          ) : (
            <Money
              currency={invoice.currency}
              minor={invoice.totalAmount}
              className="font-semibold"
            />
          )}
        </Field>

        {/*
          Every amount above is in the company's currency. The document's own
          currency is shown beside it when they disagree, which blocks
          submission until the invoice is moved or the reading is confirmed.
        */}
        <Field label="Currency" confidence={confidence('currency')}>
          <p className="text-sm font-medium">{invoice.currency}</p>
          {mismatch ? (
            <div className="notice-warning mt-2 text-xs">
              The document reads as {String(documentCurrency ?? 'another currency')}, but this
              company books in {invoice.currency}.
              {editable ? (
                <button
                  type="button"
                  className="btn-secondary mt-2 block text-xs"
                  disabled={confirmCurrency.isPending}
                  onClick={() => confirmCurrency.mutate()}
                >
                  It is billed in {invoice.currency}
                </button>
              ) : null}
            </div>
          ) : null}
        </Field>

        {/* Typed in by hand when extraction misread it, or found none. */}
        <Field
          label={company.india ? 'GSTIN' : 'Tax ID'}
          confidence={confidence('gstin')}
          edit={marks.gstin}
        >
          {editable ? (
            <input
              className={`input font-mono uppercase${tint('gstin')}`}
              aria-label={company.india ? 'GSTIN' : 'Tax ID'}
              maxLength={20}
              placeholder={company.india ? 'e.g. 29ABCDE1234F1Z5' : undefined}
              value={draft.gstin}
              onChange={(event) => setDraft({ ...draft, gstin: event.target.value })}
            />
          ) : (
            <p className="text-sm">{invoice.gstin ?? '—'}</p>
          )}
        </Field>

        {/* India only: read off the invoice, else taken from the GSTIN. */}
        {company.india ? (
          <Field label="PAN" confidence={confidence('pan')} edit={marks.pan}>
            {editable ? (
              <input
                className={`input font-mono uppercase${tint('pan')}`}
                aria-label="PAN"
                maxLength={10}
                placeholder="e.g. ABCDE1234F"
                value={draft.pan}
                onChange={(event) => setDraft({ ...draft, pan: event.target.value })}
              />
            ) : (
              <p className="text-sm">{invoice.pan ?? '—'}</p>
            )}
          </Field>
        ) : null}

        {/* Net payable and editable TDS stay visible during invoice review. */}
        {editable ? (
          // Before submission the deduction is keyed here, from the TDS Sections list.
          <div className="space-y-2 sm:col-span-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-600"
                  aria-label="Deduct TDS"
                  checked={tdsOn}
                  onChange={(event) => {
                    const yes = event.target.checked;
                    setTdsOn(yes);
                    if (yes)
                      setTdsDrafts((current) => withVendorProfiles(current, tdsProfiles, taxable));
                  }}
                />
                <span>Deduct TDS</span>
              </label>
              <span className="text-xs text-slate-500">
                Calculated on taxable value, excluding GST.
              </span>
            </div>
            {tdsOn ? (
              <TdsLinesEditor
                lines={tdsDrafts}
                setLines={setTdsDrafts}
                profiles={tdsProfiles}
                taxable={taxable}
                sections={tdsSections}
                ratesFor={ratesFor}
                successorOf={successorOf}
                format={format}
              />
            ) : null}
          </div>
        ) : !showTds ? null : invoice.tdsApplicable && (invoice.tdsLines?.length ?? 0) > 1 ? (
          // Several deductions: each with its own section, rate and base.
          <Field label="TDS" confidence={undefined}>
            {/* Each item withheld on its own amount, under its own section. */}
            <ul className="space-y-1 text-sm">
              {invoice.tdsLines!.map((line, index) => (
                <li key={index} className="text-amber-700">
                  <span className="font-medium">{line.section}</span>
                  {line.label ? ` · ${line.label}` : ''}:{' '}
                  <Money currency={invoice.currency} minor={line.amount} />
                  <span className="text-xs text-slate-500">
                    {' '}
                    ({formatBasisPoints(line.rateBasisPoints)} on{' '}
                    <Money currency={invoice.currency} minor={line.baseAmount} />)
                  </span>
                </li>
              ))}
              <li className="border-t border-slate-200 pt-1 font-semibold text-amber-700">
                TDS total <Money currency={invoice.currency} minor={invoice.tdsAmount} />
              </li>
            </ul>
            <OtherTdsProfiles
              invoice={invoice}
              profiles={vendors.find((vendor) => vendor.id === invoice.vendorId)?.tdsProfiles}
            />
          </Field>
        ) : invoice.tdsApplicable ? (
          <Field
            label={`TDS${invoice.tdsSection ? ` (${invoice.tdsSection})` : ''}`}
            confidence={undefined}
          >
            <p className="text-sm text-amber-700">
              {invoice.tdsLines?.[0]?.label ? `${invoice.tdsLines[0].label}: ` : ''}
              <Money currency={invoice.currency} minor={invoice.tdsAmount} />
              {invoice.tdsRateBasisPoints ? (
                <span className="text-xs text-slate-500">
                  {' '}
                  ({formatBasisPoints(invoice.tdsRateBasisPoints)}
                  {invoice.tdsBaseAmount ? (
                    <>
                      {' '}
                      on <Money currency={invoice.currency} minor={invoice.tdsBaseAmount} />
                    </>
                  ) : null}
                  )
                </span>
              ) : null}
            </p>
            <OtherTdsProfiles
              invoice={invoice}
              profiles={vendors.find((vendor) => vendor.id === invoice.vendorId)?.tdsProfiles}
            />
          </Field>
        ) : (
          <Field label="TDS" confidence={undefined}>
            <p className="text-sm text-slate-500">Not deducted</p>
            <OtherTdsProfiles
              invoice={invoice}
              profiles={vendors.find((vendor) => vendor.id === invoice.vendorId)?.tdsProfiles}
            />
          </Field>
        )}

        {invoice.advanceAdjusted > 0 ? (
          <Field label="Adjusted from advance" confidence={undefined}>
            <p className="text-sm text-brand-700">
              <Money currency={invoice.currency} minor={invoice.advanceAdjusted} />
              <span className="block text-xs text-slate-500">
                {invoice.status === 'SETTLED_BY_ADVANCE'
                  ? 'Settled in full from the vendor’s advance — no bank payment.'
                  : 'Taken from the vendor’s advance at accounting; the bank pays the rest.'}
              </span>
            </p>
          </Field>
        ) : null}

        <Field label="Net payable" confidence={undefined}>
          <Money
            currency={invoice.currency}
            minor={netPayable}
            className="font-semibold text-emerald-700"
          />
          {netUnsaved ? (
            <span className="block text-xs text-amber-700">
              Changed here, not saved yet — saved:{' '}
              <Money currency={invoice.currency} minor={invoice.netPayable} />
            </span>
          ) : null}
        </Field>

        {/* What accounting tagged it with when verifying; read-only here. */}
        {invoice.accounting?.verifiedAt ? (
          <>
            <Field label="Expense ledger" confidence={undefined}>
              <p className="text-sm">
                {invoice.accounting.expenseLedger ? (
                  <LedgerLabel ledger={invoice.accounting.expenseLedger} />
                ) : invoice.accounting.glCode ? (
                  <>
                    {invoice.accounting.glCode}
                    <span className="block text-xs text-slate-500">
                      GL code typed before ledgers
                    </span>
                  </>
                ) : (
                  <span className="text-slate-500">Not tagged</span>
                )}
              </p>
            </Field>
            <Field label="Vendor account" confidence={undefined}>
              <p className="text-sm">
                {invoice.accounting.partyLedger ? (
                  <LedgerLabel ledger={invoice.accounting.partyLedger} />
                ) : (
                  <span className="text-slate-500">Not tagged</span>
                )}
              </p>
            </Field>
            {invoice.accounting.advanceLedger ? (
              <Field label="Advance account" confidence={undefined}>
                <p className="text-sm">
                  <LedgerLabel ledger={invoice.accounting.advanceLedger} />
                </p>
              </Field>
            ) : null}
            {invoice.accounting.costCentre ? (
              <Field label="Cost centre" confidence={undefined}>
                <p className="text-sm">{invoice.accounting.costCentre}</p>
              </Field>
            ) : null}
          </>
        ) : null}
      </div>

      {editable ? (
        <div className="mt-5 flex items-center gap-3">
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
          {save.isSuccess ? <span className="text-sm text-emerald-700">Saved</span> : null}
          {save.error ? (
            <span className="text-sm text-red-700">{describeError(save.error)}</span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/**
 * The vendor's other TDS profiles, for information only: the deduction above
 * uses the default one, and the accounting team may split it across these
 * (fees and rent each on their own part of the bill). Nothing here changes
 * the net payable.
 */
function OtherTdsProfiles({
  invoice,
  profiles,
}: {
  invoice: Invoice;
  profiles: TdsProfile[] | undefined;
}) {
  const lines = invoice.tdsLines ?? [];
  const others = (profiles ?? []).filter(
    (profile) =>
      !lines.some((line) =>
        line.label
          ? line.label === profile.label
          : line.section === profile.section && line.rateBasisPoints === profile.rateBasisPoints,
      ),
  );
  if (!others.length) return null;
  return (
    <div className="mt-1.5 text-xs text-slate-500">
      <p>Also on file for this vendor — accounting may split the deduction:</p>
      <ul className="mt-0.5 space-y-0.5">
        {others.map((profile) => (
          <li key={profile.label}>
            {profile.label} · {profile.section} · {formatBasisPoints(profile.rateBasisPoints)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The coding string: which department and location the spend belongs to.
 *
 * Only the two leaves are chosen. Business unit, vertical and region follow
 * from them on the server and are shown here read-only, and the whole tuple
 * is frozen once the invoice is submitted — reports filter on it, and a
 * later reorganisation must not re-code this invoice.
 *
 * The expense ledger here is only a suggestion — blank, it falls back to the
 * vendor's default. Accounting sees it pre-filled and decides at verification.
 */
function InvoiceCoding({
  invoice,
  editable,
  frozen,
}: {
  invoice: Invoice;
  editable: boolean;
  /** Past review: the tuple is fixed for good, not merely read-only to this user. */
  frozen: boolean;
}) {
  const queryClient = useQueryClient();
  const query = { companyId: invoice.companyId, pageSize: 200 };
  const key = (name: string) => [name, 'for-invoice-coding', invoice.companyId];
  const departments = useQuery({
    queryKey: key('departments'),
    queryFn: () => api.settings.departments(query),
  }).data?.items;
  const locations = useQuery({
    queryKey: key('locations'),
    queryFn: () => api.settings.locations(query),
  }).data?.items;
  const units = useQuery({
    queryKey: key('business-units'),
    queryFn: () => api.settings.businessUnits(query),
  }).data?.items;
  const verticals = useQuery({
    queryKey: key('verticals'),
    queryFn: () => api.settings.verticals(query),
  }).data?.items;
  const regions = useQuery({
    queryKey: key('regions'),
    queryFn: () => api.settings.regions(query),
  }).data?.items;

  const [departmentId, setDepartmentId] = useState(invoice.departmentId ?? '');
  const [locationId, setLocationId] = useState(invoice.locationId ?? '');

  const save = useMutation({
    mutationFn: () => api.invoices.update(invoice.id, { departmentId, locationId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] }),
  });

  const nameIn = (list: Array<{ id: string; name: string }> | undefined, id?: string) =>
    (id && list?.find((item) => item.id === id)?.name) || '—';
  const changed =
    departmentId !== (invoice.departmentId ?? '') || locationId !== (invoice.locationId ?? '');

  return (
    <Card className="mt-6 p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold">Coding</h2>
        {frozen ? <span className="text-xs text-slate-500">Frozen once submitted</span> : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Department">
          {editable ? (
            <select
              className="input"
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
            >
              <option value="">Not coded</option>
              {(departments ?? [])
                .filter((department) => department.active || department.id === departmentId)
                .map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
            </select>
          ) : (
            <p className="text-sm">{nameIn(departments, invoice.departmentId)}</p>
          )}
        </Field>
        <Field label="Business unit">
          <p className="text-sm">{nameIn(units, invoice.businessUnitId)}</p>
        </Field>
        <Field label="Vertical">
          <p className="text-sm">{nameIn(verticals, invoice.verticalId)}</p>
        </Field>
        <Field label="Location">
          {editable ? (
            <select
              className="input"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Not coded</option>
              {(locations ?? [])
                .filter((location) => location.active || location.id === locationId)
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
            </select>
          ) : (
            <p className="text-sm">{nameIn(locations, invoice.locationId)}</p>
          )}
        </Field>
        <Field label="Region">
          <p className="text-sm">{nameIn(regions, invoice.regionId)}</p>
        </Field>
      </div>
      {editable ? (
        <div className="mt-5 flex items-center gap-3">
          <button
            className="btn-primary"
            disabled={!changed || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save coding'}
          </button>
          <span className="text-xs text-slate-500">
            Unit, vertical and region follow from the department and location.
          </span>
          {save.error ? (
            <span className="text-sm text-red-700">{describeError(save.error)}</span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** A field whose value came from a person rather than from extraction. */
interface Provenance {
  /** `edited`: changed from what was read; `manual`: extraction read nothing. */
  kind: 'edited' | 'manual';
  /** Changed in the form and not saved yet. */
  unsaved: boolean;
  /** What extraction read, for display. */
  original?: React.ReactNode;
  /** Puts the reading back in the form. */
  onRevert?: () => void;
}

function Field({
  label,
  confidence,
  learned,
  edit,
  children,
}: {
  label: string;
  confidence?: number;
  /** Read where a reviewer's earlier correction showed this supplier prints it. */
  learned?: boolean;
  /** Set when a person supplied the value: replaces the confidence, which was the machine's. */
  edit?: Provenance;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        {edit ? (
          <EditedBadge
            kind={edit.kind}
            title={
              edit.unsaved
                ? 'Changed here, not saved yet'
                : edit.kind === 'edited'
                  ? 'Changed by a reviewer from what extraction read'
                  : 'Typed in by a reviewer; extraction read nothing here'
            }
          />
        ) : (
          <>
            {confidence !== undefined ? (
              <ConfidenceBadge
                value={confidence}
                prefix="Auto"
                title="Filled in by extraction; the percentage is how sure it was"
              />
            ) : null}
            {learned ? (
              <span
                className="badge badge-progress rounded-md px-1.5"
                title="Read where an earlier correction showed this supplier prints it"
              >
                Learned
              </span>
            ) : null}
          </>
        )}
        {edit?.unsaved ? <span className="text-xs text-slate-500">Unsaved</span> : null}
      </div>
      {children}
      {edit?.kind === 'edited' && edit.original !== undefined ? (
        <p className="mt-1 text-xs text-slate-500">
          Extracted: <span className="text-slate-700">{edit.original}</span>
          {edit.onRevert ? (
            <>
              {' · '}
              <button
                type="button"
                className="font-medium text-brand-700 hover:underline"
                onClick={edit.onRevert}
              >
                Revert
              </button>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Renders the source document.
 *
 * The API either returns a signed URL (Azure) or streams the bytes (local
 * disk), so the viewer fetches through the authenticated client and falls
 * back to an object URL when there is no direct link.
 */
function DocumentViewer({ invoiceId, fileName }: { invoiceId: string; fileName?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    apiClient
      .download(`/invoices/${invoiceId}/document`)
      .then((blob) => {
        if (cancelled) return;
        // A JSON body means the driver issued a signed URL instead of bytes.
        if (blob.type.includes('json')) {
          return blob.text().then((text) => {
            const parsed = JSON.parse(text) as { url?: string };
            if (parsed.url) setUrl(parsed.url);
          });
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceId]);

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="font-semibold">Document</h2>
        <span className="truncate text-xs text-slate-500">{fileName}</span>
      </div>
      {failed ? (
        <p className="p-6 text-sm text-slate-500">The original document could not be loaded.</p>
      ) : !url ? (
        <Spinner label="Loading document…" />
      ) : (
        <iframe title="Invoice document" src={url} className="h-[640px] w-full" />
      )}
    </Card>
  );
}

function ResolveFindingModal({
  invoiceId,
  finding,
  onClose,
}: {
  invoiceId: string;
  finding: ValidationFinding;
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  const [resolution, setResolution] = useState<'KEEP' | 'DUPLICATE'>('KEEP');
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.invoices.resolveFinding(invoiceId, { code: finding.code, resolution, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      onClose();
    },
  });

  const isDuplicate = finding.code.includes('DUPLICATE');

  return (
    <Modal
      title="Resolve finding"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={note.trim().length < 3 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Record decision'}
          </button>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-600">{finding.message}</p>

      {isDuplicate ? (
        <div className="mb-4 space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              className="mt-1"
              checked={resolution === 'KEEP'}
              onChange={() => setResolution('KEEP')}
            />
            <span>
              <span className="font-medium">This is not a duplicate</span>
              <span className="block text-slate-500">Continue processing this invoice.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              className="mt-1"
              checked={resolution === 'DUPLICATE'}
              onChange={() => setResolution('DUPLICATE')}
            />
            <span>
              <span className="font-medium">This is a duplicate</span>
              <span className="block text-slate-500">Mark it as such so it can never be paid.</span>
            </span>
          </label>
        </div>
      ) : null}

      <label className="label" htmlFor="note">
        Reason (recorded in the audit trail)
      </label>
      <textarea
        id="note"
        className="input"
        rows={3}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Why is this being resolved this way?"
      />
      {mutation.error ? (
        <div className="mt-3">
          <ErrorState error={mutation.error} />
        </div>
      ) : null}
    </Modal>
  );
}

/** A tagged ledger: its code, then its name as it was when the invoice was tagged. */
function LedgerLabel({ ledger }: { ledger: LedgerSnapshot }) {
  return (
    <>
      <span className="font-mono text-xs text-slate-500">{ledger.code}</span> {ledger.name}
    </>
  );
}
